/**
 * Safe Sandbox - Production-grade secure JavaScript execution environment
 * 
 * This service provides a controlled execution environment that safely runs
 * AI-generated timeline manipulation code with comprehensive security measures,
 * resource monitoring, and integration with Docker-based backend isolation.
 */

import timelineAPI from './timelineAPI.js';

class SafeExecutionSandbox {
  constructor() {
    this.executionHistory = [];
    this.maxHistorySize = 50;
    this.executionTimeoutMs = 30000; // 30 second timeout (increased for production)
    this.allowedGlobals = new Set([
      'console', 'Math', 'Date', 'Array', 'Object', 'String', 'Number', 'Boolean',
      'JSON', 'parseInt', 'parseFloat', 'isNaN', 'isFinite'
    ]);
    
    // Production configuration
    this.useDockerBackend = true;
    this.maxCodeLength = 50000;
    this.maxOperationsPerExecution = 10000;
    this.resourceMonitoring = true;
    this.auditLogging = true;
    
    // Safe console implementation
    this.safeConsole = {
      log: (...args) => this._logMessage('info', args),
      error: (...args) => this._logMessage('error', args),
      warn: (...args) => this._logMessage('warn', args),
      info: (...args) => this._logMessage('info', args)
    };
    
    this.executionLogs = [];
    this.logListeners = new Set();
    this.resourceMetrics = [];
    this.securityViolations = [];
    
    // Initialize security monitoring
    this._initializeSecurityMonitoring();
  }

  /**
   * Execute AI-generated timeline code safely with production security measures
   */
  async executeTimelineCode(code, timelineContext = {}) {
    const executionId = this._generateExecutionId();
    const startTime = Date.now();
    
    try {
      // Clear logs for new execution
      this.executionLogs = [];
      this.resourceMetrics = [];
      
      // Enhanced validation with input sanitization
      const validation = await this._validateCodeProduction(code, timelineContext);
      if (!validation.isValid) {
        this._recordSecurityViolation(executionId, validation.securityIssues);
        throw new Error(`Code validation failed: ${validation.errors.join(', ')}`);
      }

      // Create snapshot of timeline state for rollback
      const timelineSnapshot = this._createTimelineSnapshot();
      
      // Resource monitoring setup
      const resourceMonitor = this._initializeResourceMonitoring();
      
      let result;
      
      // Choose execution method based on configuration
      if (this.useDockerBackend) {
        result = await this._executeInDockerBackend(code, timelineContext, executionId);
      } else {
        // Fallback to frontend execution with enhanced security
        const context = this._createSafeContext(timelineContext);
        result = await this._executeWithTimeout(code, context, executionId);
      }
      
      const executionTime = Date.now() - startTime;
      
      // Collect resource metrics
      const resourceUsage = this._collectResourceMetrics(resourceMonitor);
      
      // Record successful execution
      const execution = {
        id: executionId,
        code: code.substring(0, 500), // Store first 500 chars
        success: true,
        result: result,
        executionTime,
        logs: [...this.executionLogs],
        timestamp: new Date().toISOString(),
        timelineStateAfter: this._createTimelineSnapshot(),
        resourceUsage,
        executionMethod: this.useDockerBackend ? 'docker' : 'frontend',
        securityScore: validation.securityScore
      };
      
      this._addToHistory(execution);
      
      // Audit logging
      if (this.auditLogging) {
        this._logAuditEvent('code_execution', execution);
      }
      
      return {
        success: true,
        executionId,
        result,
        logs: this.executionLogs,
        executionTime,
        timelineChanged: this._timelineStateChanged(timelineSnapshot),
        resourceUsage,
        securityScore: validation.securityScore
      };

    } catch (error) {
      const executionTime = Date.now() - startTime;
      
      // Record failed execution
      const execution = {
        id: executionId,
        code: code.substring(0, 500),
        success: false,
        error: error.message,
        executionTime,
        logs: [...this.executionLogs],
        timestamp: new Date().toISOString(),
        executionMethod: this.useDockerBackend ? 'docker' : 'frontend'
      };
      
      this._addToHistory(execution);
      
      // Audit logging for failures
      if (this.auditLogging) {
        this._logAuditEvent('code_execution_failed', execution);
      }
      
      // Rollback timeline changes if any were made
      // (Timeline API handles its own undo/redo, so this is mainly for safety)
      
      return {
        success: false,
        executionId,
        error: error.message,
        logs: this.executionLogs,
        executionTime
      };
    }
  }

  /**
   * Validate code for syntax and security issues
   */
  _validateCode(code) {
    const errors = [];
    
    // Check for dangerous patterns
    const dangerousPatterns = [
      { pattern: /\beval\s*\(/gi, message: 'eval() is not allowed' },
      { pattern: /\bFunction\s*\(/gi, message: 'Function constructor is not allowed' },
      { pattern: /\bsetTimeout\s*\(/gi, message: 'setTimeout is not allowed' },
      { pattern: /\bsetInterval\s*\(/gi, message: 'setInterval is not allowed' },
      { pattern: /\bimport\s+/gi, message: 'import statements are not allowed' },
      { pattern: /\brequire\s*\(/gi, message: 'require() is not allowed' },
      { pattern: /\bfetch\s*\(/gi, message: 'fetch() is not allowed' },
      { pattern: /\bXMLHttpRequest/gi, message: 'XMLHttpRequest is not allowed' },
      { pattern: /\bdocument\./gi, message: 'DOM access is not allowed' },
      { pattern: /\bwindow\./gi, message: 'window object access is not allowed' },
      { pattern: /\blocal\w*Storage/gi, message: 'Storage access is not allowed' },
      { pattern: /\b__\w*__/gi, message: 'Dunder methods are not allowed' },
      { pattern: /\bwhile\s*\(\s*true\s*\)/gi, message: 'Infinite loops are not allowed' },
      { pattern: /\bfor\s*\(\s*;\s*;\s*\)/gi, message: 'Infinite loops are not allowed' }
    ];

    dangerousPatterns.forEach(({ pattern, message }) => {
      if (pattern.test(code)) {
        errors.push(message);
      }
    });

    // Check for allowed timeline API usage
    const timelineApiPattern = /timeline\.(\w+)/g;
    const allowedMethods = new Set([
      'addClip', 'moveClip', 'removeClip', 'getClips', 'clear', 'getDuration',
      'selectClips', 'modifySelected', 'findClipsByTag', 'splitClip', 'trimClip',
      'setAspectRatio', 'getCompositionAtTime', 'analyzeTimeline', 'getClipsInRange',
      'getAspectRatio'
    ]);

    let match;
    while ((match = timelineApiPattern.exec(code)) !== null) {
      const method = match[1];
      if (!allowedMethods.has(method)) {
        errors.push(`timeline.${method}() is not allowed`);
      }
    }

    // Basic syntax check
    try {
      new Function(code);
    } catch (syntaxError) {
      errors.push(`Syntax error: ${syntaxError.message}`);
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Create safe execution context
   */
  _createSafeContext(timelineContext) {
    const context = {
      // Timeline API
      timeline: timelineAPI,
      
      // Safe console
      console: this.safeConsole,
      
      // Safe global objects
      Math: Math,
      Date: Date,
      Array: Array,
      Object: Object,
      String: String,
      Number: Number,
      Boolean: Boolean,
      JSON: JSON,
      parseInt: parseInt,
      parseFloat: parseFloat,
      isNaN: isNaN,
      isFinite: isFinite,
      
      // Timeline context data
      context: timelineContext
    };

    return context;
  }

  /**
   * Execute code with timeout protection
   */
  async _executeWithTimeout(code, context, executionId) {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error(`Execution timeout (${this.executionTimeoutMs}ms exceeded)`));
      }, this.executionTimeoutMs);

      try {
        // Create isolated execution function
        const wrappedCode = this._wrapCode(code);
        const executionFunction = this._createExecutionFunction(wrappedCode, context);
        
        // Execute the code
        const result = executionFunction();
        
        clearTimeout(timeout);
        resolve(result);
        
      } catch (error) {
        clearTimeout(timeout);
        reject(error);
      }
    });
  }

  /**
   * Wrap code with safety checks and monitoring
   */
  _wrapCode(code) {
    return `
(function() {
  "use strict";
  
  // Execution monitoring
  let operationCount = 0;
  const maxOperations = 10000;
  
  // Override loop constructs to prevent infinite loops
  const originalForEach = Array.prototype.forEach;
  Array.prototype.forEach = function(callback, thisArg) {
    if (++operationCount > maxOperations) {
      throw new Error('Maximum operations exceeded');
    }
    return originalForEach.call(this, callback, thisArg);
  };
  
  try {
    ${code}
  } finally {
    // Restore original methods
    Array.prototype.forEach = originalForEach;
  }
})();
    `;
  }

  /**
   * Create execution function with controlled scope
   */
  _createExecutionFunction(wrappedCode, context) {
    // Create function with controlled scope
    const contextKeys = Object.keys(context);
    const contextValues = Object.values(context);
    
    try {
      // Use Function constructor with limited scope
      const func = new Function(...contextKeys, wrappedCode);
      return () => func.apply(null, contextValues);
    } catch (error) {
      throw new Error(`Failed to create execution function: ${error.message}`);
    }
  }

  /**
   * Create timeline state snapshot for rollback
   */
  _createTimelineSnapshot() {
    return {
      clips: JSON.parse(JSON.stringify(timelineAPI.getClips())),
      duration: timelineAPI.getDuration(),
      aspectRatio: timelineAPI.getAspectRatio(),
      selectedClipIds: [...timelineAPI.selectedClipIds]
    };
  }

  /**
   * Check if timeline state has changed
   */
  _timelineStateChanged(beforeSnapshot) {
    const afterSnapshot = this._createTimelineSnapshot();
    
    return (
      JSON.stringify(beforeSnapshot.clips) !== JSON.stringify(afterSnapshot.clips) ||
      beforeSnapshot.duration !== afterSnapshot.duration ||
      beforeSnapshot.aspectRatio !== afterSnapshot.aspectRatio ||
      JSON.stringify(beforeSnapshot.selectedClipIds) !== JSON.stringify(afterSnapshot.selectedClipIds)
    );
  }

  /**
   * Log execution messages
   */
  _logMessage(level, args) {
    const message = args.map(arg => 
      typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
    ).join(' ');
    
    const logEntry = {
      level,
      message,
      timestamp: Date.now()
    };
    
    this.executionLogs.push(logEntry);
    
    // Notify listeners
    this.logListeners.forEach(listener => {
      try {
        listener(logEntry);
      } catch (error) {
        console.error('Log listener error:', error);
      }
    });
    
    // Also log to real console in development
    if (process.env.NODE_ENV === 'development') {
      console[level](`[Sandbox ${level.toUpperCase()}]`, message);
    }
  }

  /**
   * Generate unique execution ID
   */
  _generateExecutionId() {
    return `exec_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Add execution to history
   */
  _addToHistory(execution) {
    this.executionHistory.unshift(execution);
    
    // Limit history size
    if (this.executionHistory.length > this.maxHistorySize) {
      this.executionHistory = this.executionHistory.slice(0, this.maxHistorySize);
    }
  }

  /**
   * Get execution history
   */
  getExecutionHistory() {
    return [...this.executionHistory];
  }

  /**
   * Get recent executions
   */
  getRecentExecutions(count = 10) {
    return this.executionHistory.slice(0, count);
  }

  /**
   * Get execution statistics
   */
  getExecutionStats() {
    const total = this.executionHistory.length;
    const successful = this.executionHistory.filter(e => e.success).length;
    const failed = total - successful;
    
    const avgExecutionTime = this.executionHistory.length > 0
      ? this.executionHistory.reduce((sum, e) => sum + e.executionTime, 0) / total
      : 0;

    return {
      total,
      successful,
      failed,
      successRate: total > 0 ? (successful / total) * 100 : 0,
      avgExecutionTime: Math.round(avgExecutionTime)
    };
  }

  /**
   * Subscribe to execution logs
   */
  subscribeToLogs(listener) {
    this.logListeners.add(listener);
    return () => this.logListeners.delete(listener);
  }

  /**
   * Clear execution history
   */
  clearHistory() {
    this.executionHistory = [];
  }

  /**
   * Test code safety without execution
   */
  testCodeSafety(code) {
    return this._validateCode(code);
  }

  /**
   * Get sandbox configuration info
   */
  getSandboxInfo() {
    return {
      timeoutMs: this.executionTimeoutMs,
      maxHistorySize: this.maxHistorySize,
      allowedGlobals: Array.from(this.allowedGlobals),
      executionCount: this.executionHistory.length,
      useDockerBackend: this.useDockerBackend,
      maxCodeLength: this.maxCodeLength,
      resourceMonitoring: this.resourceMonitoring,
      auditLogging: this.auditLogging,
      securityViolations: this.securityViolations.length
    };
  }

  /**
   * Initialize security monitoring
   */
  _initializeSecurityMonitoring() {
    // Security event listeners
    this.securityEventListeners = new Set();
    
    // Threat detection patterns
    this.threatPatterns = {
      'code_injection': /eval\s*\(|Function\s*\(|new\s+Function/gi,
      'infinite_loop': /while\s*\(\s*true\s*\)|for\s*\(\s*;\s*;\s*\)/gi,
      'memory_bomb': /new\s+Array\s*\(\s*\d{6,}\s*\)/gi,
      'prototype_pollution': /prototype\s*\[|__proto__\s*\[/gi,
      'dom_access': /document\.|window\.|navigator\./gi,
      'network_access': /fetch\s*\(|XMLHttpRequest|WebSocket/gi
    };
    
    // Initialize monitoring intervals
    this._startSecurityMonitoring();
  }

  /**
   * Enhanced production-level code validation
   */
  async _validateCodeProduction(code, context) {
    // Input length validation
    if (code.length > this.maxCodeLength) {
      return {
        isValid: false,
        errors: [`Code too long: ${code.length} characters (max: ${this.maxCodeLength})`],
        securityIssues: ['code_length_exceeded'],
        securityScore: 0
      };
    }

    // Start with basic validation
    const basicValidation = this._validateCode(code);
    const errors = [...basicValidation.errors];
    const securityIssues = [];
    let securityScore = 100;

    // Advanced threat detection
    for (const [threatType, pattern] of Object.entries(this.threatPatterns)) {
      if (pattern.test(code)) {
        securityIssues.push(threatType);
        securityScore -= 20;
        errors.push(`Security threat detected: ${threatType}`);
      }
    }

    // Pattern analysis for suspicious activity
    const suspiciousPatterns = [
      { pattern: /\b(btoa|atob)\s*\(/gi, penalty: 10, message: 'Base64 encoding detected' },
      { pattern: /\bunescape\s*\(/gi, penalty: 15, message: 'URL unescape detected' },
      { pattern: /\bdecodeURI\w*\s*\(/gi, penalty: 10, message: 'URI decoding detected' },
      { pattern: /\bJSON\s*\.\s*parse\s*\([^)]*\)/gi, penalty: 5, message: 'JSON parsing detected' },
      { pattern: /\bsetTimeout\s*\(/gi, penalty: 25, message: 'setTimeout detected' },
      { pattern: /\bsetInterval\s*\(/gi, penalty: 25, message: 'setInterval detected' }
    ];

    for (const { pattern, penalty, message } of suspiciousPatterns) {
      if (pattern.test(code)) {
        securityScore -= penalty;
        errors.push(`Suspicious pattern: ${message}`);
      }
    }

    // Complexity analysis
    const complexityScore = this._analyzeCodeComplexity(code);
    if (complexityScore > 100) {
      securityScore -= Math.min(complexityScore - 100, 30);
      errors.push(`High code complexity: ${complexityScore}`);
    }

    // API call frequency analysis
    const apiCalls = (code.match(/timeline\.\w+/g) || []).length;
    if (apiCalls > 50) {
      securityScore -= Math.min((apiCalls - 50) * 2, 30);
      errors.push(`Too many API calls: ${apiCalls}`);
    }

    return {
      isValid: errors.length === 0,
      errors,
      securityIssues,
      securityScore: Math.max(0, securityScore)
    };
  }

  /**
   * Execute code in Docker backend
   */
  async _executeInDockerBackend(code, context, executionId) {
    try {
      const response = await fetch('/api/execute-code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code,
          context,
          executionId,
          options: {
            timeout: this.executionTimeoutMs,
            resourceLimits: {
              memory: '128m',
              cpu: 0.5
            }
          }
        })
      });

      if (!response.ok) {
        throw new Error(`Backend execution failed: ${response.status}`);
      }

      const result = await response.json();
      
      // Process backend logs
      if (result.logs) {
        result.logs.forEach(log => {
          this._logMessage(log.level, [log.message]);
        });
      }

      return result.result;
    } catch (error) {
      throw new Error(`Docker backend execution failed: ${error.message}`);
    }
  }

  /**
   * Initialize resource monitoring
   */
  _initializeResourceMonitoring() {
    const startTime = performance.now();
    const startMemory = performance.memory ? performance.memory.usedJSHeapSize : 0;
    
    return {
      startTime,
      startMemory,
      samples: []
    };
  }

  /**
   * Collect resource metrics
   */
  _collectResourceMetrics(monitor) {
    const endTime = performance.now();
    const endMemory = performance.memory ? performance.memory.usedJSHeapSize : 0;
    
    return {
      executionTime: endTime - monitor.startTime,
      memoryUsage: endMemory - monitor.startMemory,
      samples: monitor.samples
    };
  }

  /**
   * Analyze code complexity
   */
  _analyzeCodeComplexity(code) {
    const metrics = {
      lines: code.split('\n').length,
      characters: code.length,
      functions: (code.match(/function\s+\w+/g) || []).length,
      loops: (code.match(/\b(for|while|do)\s*\(/g) || []).length,
      conditionals: (code.match(/\b(if|else|switch)\s*\(/g) || []).length,
      apiCalls: (code.match(/timeline\.\w+/g) || []).length
    };

    // Calculate complexity score
    return (
      metrics.lines * 0.5 +
      metrics.functions * 5 +
      metrics.loops * 10 +
      metrics.conditionals * 3 +
      metrics.apiCalls * 2
    );
  }

  /**
   * Record security violation
   */
  _recordSecurityViolation(executionId, violations) {
    const violation = {
      id: executionId,
      violations,
      timestamp: new Date().toISOString(),
      severity: this._calculateViolationSeverity(violations)
    };

    this.securityViolations.push(violation);
    
    // Limit violation history
    if (this.securityViolations.length > 100) {
      this.securityViolations = this.securityViolations.slice(-100);
    }

    // Notify security event listeners
    this.securityEventListeners.forEach(listener => {
      try {
        listener('security_violation', violation);
      } catch (error) {
        console.error('Security listener error:', error);
      }
    });
  }

  /**
   * Calculate violation severity
   */
  _calculateViolationSeverity(violations) {
    const severityMap = {
      'code_injection': 'critical',
      'infinite_loop': 'high',
      'memory_bomb': 'critical',
      'prototype_pollution': 'high',
      'dom_access': 'medium',
      'network_access': 'high',
      'code_length_exceeded': 'medium'
    };

    const severities = violations.map(v => severityMap[v] || 'low');
    
    if (severities.includes('critical')) return 'critical';
    if (severities.includes('high')) return 'high';
    if (severities.includes('medium')) return 'medium';
    return 'low';
  }

  /**
   * Start security monitoring
   */
  _startSecurityMonitoring() {
    // Monitor execution patterns
    setInterval(() => {
      this._analyzeExecutionPatterns();
    }, 60000); // Every minute

    // Monitor resource usage
    setInterval(() => {
      this._monitorResourceUsage();
    }, 5000); // Every 5 seconds
  }

  /**
   * Analyze execution patterns for anomalies
   */
  _analyzeExecutionPatterns() {
    const recent = this.executionHistory.slice(0, 10);
    const failures = recent.filter(e => !e.success);
    
    // Check for high failure rate
    if (failures.length > 7) {
      this._recordSecurityViolation('pattern_analysis', ['high_failure_rate']);
    }

    // Check for suspicious timing patterns
    const rapidExecutions = recent.filter(e => 
      Date.now() - new Date(e.timestamp).getTime() < 5000
    );
    
    if (rapidExecutions.length > 5) {
      this._recordSecurityViolation('pattern_analysis', ['rapid_execution']);
    }
  }

  /**
   * Monitor resource usage
   */
  _monitorResourceUsage() {
    if (performance.memory) {
      const memoryUsage = performance.memory.usedJSHeapSize;
      const memoryLimit = 100 * 1024 * 1024; // 100MB
      
      if (memoryUsage > memoryLimit) {
        this._recordSecurityViolation('resource_monitor', ['memory_limit_exceeded']);
      }
    }
  }

  /**
   * Log audit event
   */
  _logAuditEvent(event, data) {
    const auditEntry = {
      event,
      data: {
        id: data.id,
        success: data.success,
        executionTime: data.executionTime,
        securityScore: data.securityScore,
        executionMethod: data.executionMethod
      },
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      sessionId: this._getSessionId()
    };

    // Send to audit log endpoint
    fetch('/api/audit-log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(auditEntry)
    }).catch(error => {
      console.warn('Audit logging failed:', error);
    });
  }

  /**
   * Get session ID for audit logging
   */
  _getSessionId() {
    let sessionId = localStorage.getItem('session_id');
    if (!sessionId) {
      sessionId = Math.random().toString(36).substr(2, 9);
      localStorage.setItem('session_id', sessionId);
    }
    return sessionId;
  }

  /**
   * Get security statistics
   */
  getSecurityStats() {
    return {
      totalViolations: this.securityViolations.length,
      violationsByType: this._groupViolationsByType(),
      recentViolations: this.securityViolations.slice(-10),
      threatLevel: this._calculateThreatLevel(),
      securityScore: this._calculateOverallSecurityScore()
    };
  }

  /**
   * Group violations by type
   */
  _groupViolationsByType() {
    const groups = {};
    this.securityViolations.forEach(violation => {
      violation.violations.forEach(v => {
        groups[v] = (groups[v] || 0) + 1;
      });
    });
    return groups;
  }

  /**
   * Calculate threat level
   */
  _calculateThreatLevel() {
    const recentViolations = this.securityViolations.filter(v => 
      Date.now() - new Date(v.timestamp).getTime() < 300000 // Last 5 minutes
    );

    const criticalCount = recentViolations.filter(v => v.severity === 'critical').length;
    const highCount = recentViolations.filter(v => v.severity === 'high').length;

    if (criticalCount > 0) return 'critical';
    if (highCount > 2) return 'high';
    if (recentViolations.length > 5) return 'medium';
    return 'low';
  }

  /**
   * Calculate overall security score
   */
  _calculateOverallSecurityScore() {
    const recent = this.executionHistory.slice(0, 20);
    if (recent.length === 0) return 100;

    const totalScore = recent.reduce((sum, e) => sum + (e.securityScore || 80), 0);
    return Math.round(totalScore / recent.length);
  }

  /**
   * Subscribe to security events
   */
  subscribeToSecurityEvents(listener) {
    this.securityEventListeners.add(listener);
    return () => this.securityEventListeners.delete(listener);
  }

  /**
   * Execute code in dry-run mode (no timeline changes)
   */
  async dryRunCode(code, timelineContext = {}) {
    // Create a mock timeline API for dry run
    const mockTimeline = this._createMockTimeline();
    const context = {
      ...this._createSafeContext(timelineContext),
      timeline: mockTimeline
    };

    try {
      const validation = this._validateCode(code);
      if (!validation.isValid) {
        return {
          success: false,
          errors: validation.errors,
          logs: []
        };
      }

      this.executionLogs = [];
      await this._executeWithTimeout(code, context, 'dry_run');

      return {
        success: true,
        logs: this.executionLogs,
        mockOperations: mockTimeline.getOperations()
      };

    } catch (error) {
      return {
        success: false,
        error: error.message,
        logs: this.executionLogs
      };
    }
  }

  /**
   * Create mock timeline API for dry runs
   */
  _createMockTimeline() {
    const operations = [];
    
    return {
      addClip: (clip, startTime) => {
        operations.push({ type: 'addClip', clip: clip?.name || 'Unknown', startTime });
        return 'mock_id';
      },
      moveClip: (clipId, newTime) => {
        operations.push({ type: 'moveClip', clipId, newTime });
        return true;
      },
      removeClip: (clipId) => {
        operations.push({ type: 'removeClip', clipId });
        return true;
      },
      getClips: () => {
        operations.push({ type: 'getClips' });
        return [];
      },
      clear: () => {
        operations.push({ type: 'clear' });
      },
      getDuration: () => {
        operations.push({ type: 'getDuration' });
        return 0;
      },
      selectClips: (clipIds) => {
        operations.push({ type: 'selectClips', clipIds });
      },
      findClipsByTag: (tag) => {
        operations.push({ type: 'findClipsByTag', tag });
        return [];
      },
      getOperations: () => operations
    };
  }
}

// Export singleton instance
const safeSandbox = new SafeExecutionSandbox();
export default safeSandbox;